/**
 * /delete-account — public page that satisfies Google Play's mandatory
 * "Delete account URL" requirement. Anyone (including signed-out users)
 * can land here. If logged in, they can confirm deletion in one tap.
 * If logged out, they get email-based instructions.
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function DeleteAccount() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const onDelete = async () => {
    if (confirm !== "DELETE") return;
    setLoading(true);
    try {
      await api.post("/auth/delete-account");
      try { await logout(); } catch (_) {}
      toast.success("Your account and all data have been permanently deleted");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete account — please try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2C332A]" data-testid="delete-account-page">
      <header className="px-4 pt-3 pb-3 flex items-center gap-3 border-b border-[#E6E2D8] bg-white/80 backdrop-blur">
        <button onClick={() => navigate(-1)} className="rounded-full p-2 hover:bg-[#E6E2D8]/60" aria-label="Back">
          <ArrowLeft size={18} strokeWidth={1.5} />
        </button>
        <h1 style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl font-light">Delete account</h1>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-8 text-sm leading-relaxed">
        <div className="rounded-2xl border border-[#E6E2D8] bg-white p-5 mb-6">
          <h2 style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-light mb-3 flex items-center gap-2">
            <AlertTriangle size={20} className="text-[#B5564B]" />
            This is permanent
          </h2>
          <p className="text-[#2C332A]/75 mb-3">
            Deleting your Fitjong account immediately removes the following data from our servers:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-[#2C332A]/85">
            <li>Your email address and password</li>
            <li>Your profile (energy, coins, daily streak, boosters)</li>
            <li>All completed levels and journey progress</li>
            <li>All game-session history and statistics</li>
            <li>Any synced step counts</li>
            <li>Active sessions on every device</li>
          </ul>
          <p className="text-[#2C332A]/75 mt-4">
            <b>Kept</b> (anonymised, retention required by UK consumer / tax law):
          </p>
          <ul className="list-disc pl-5 space-y-1 text-[#2C332A]/85">
            <li>In-app purchase records (kept for 6 years for tax + refund eligibility, with your user ID replaced by an anonymised token).</li>
          </ul>
          <p className="text-[#2C332A]/75 mt-4">
            We do not retain any other data beyond the deletion request. The deletion is immediate and cannot be undone.
          </p>
        </div>

        {user ? (
          <div className="rounded-2xl border border-[#B5564B]/30 bg-white p-5">
            <p className="text-sm text-[#2C332A]/85 mb-3">
              You're signed in as <b>{user.email}</b>. Type <b>DELETE</b> below to confirm.
            </p>
            <Input
              data-testid="delete-confirm-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="mb-4"
            />
            <Button
              data-testid="delete-account-btn"
              onClick={onDelete}
              disabled={confirm !== "DELETE" || loading}
              className="w-full rounded-full bg-[#B5564B] hover:bg-[#9F4537] text-white h-11"
            >
              <Trash2 size={16} strokeWidth={1.6} className="mr-2" />
              {loading ? "Deleting…" : "Permanently delete my account"}
            </Button>
          </div>
        ) : (
          <div className="rounded-2xl border border-[#E6E2D8] bg-white p-5">
            <p className="text-sm text-[#2C332A]/85 mb-3">
              You're not signed in. Two options:
            </p>
            <ol className="list-decimal pl-5 space-y-2 text-[#2C332A]/85 mb-4">
              <li>
                <Link to="/login" className="text-[#5F7161] underline">
                  Sign in
                </Link>{" "}
                and return to this page — deletion happens in one tap.
              </li>
              <li>
                Email <a href="mailto:hello@fitjong.co.uk" className="text-[#5F7161] underline">hello@fitjong.co.uk</a> from
                your registered address with the subject line "Delete my account". We process requests within 7 days.
              </li>
            </ol>
            <Link to="/login">
              <Button className="w-full rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white h-11">
                Sign in to delete
              </Button>
            </Link>
          </div>
        )}
      </main>

      <footer className="text-center text-xs text-[#2C332A]/50 pb-10">
        <Link to="/privacy" className="underline">Privacy Policy</Link>
        {" · "}
        <Link to="/terms" className="underline">Terms of Service</Link>
      </footer>
    </div>
  );
}
